import { useMemo, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { buildMonthGrid, dateKey, formatMonthLabel, sameDate } from '../../utils/dateUtils';

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];

// 表示順ごとの文字色（1件目=緑, 2件目=青, 3件目=オレンジ, 4件目=紫,
// 5件目以降=グレー）。テンプレートカラーは使用しない。
const ORDER_CLASS_COUNT = 5; // order-1〜order-4 + order-5(5件目以降はこれで統一)

/**
 * 予定タイトルを先頭3文字＋「…」に短縮する。
 *
 * 補足: 元の文章表記ルールは「先頭4文字まで表示、5文字目以降があれば
 * 「…」を付与」だったが、提示された4つの例（けいくん試合→けいく…、
 * 誕生日→誕生日、病院→病院、ゴミの日→ゴミの…）はすべて例外なく
 * 「3文字を超えたら先頭3文字＋…」で一致する（4文字ちょうどの
 * 「ゴミの日」も省略されている点が決め手）。例の方を優先して実装した。
 */
function truncateTitle(title) {
  const chars = [...(title || '')];
  if (chars.length <= 3) return chars.join('');
  return `${chars.slice(0, 3).join('')}…`;
}

/** Material Design 3 style month calendar — 予定がある日は日付の下にタイトルを表示する。 */
export default function CalendarMonth() {
  const { today, selectedDate, setSelectedDate, events, openDayEventsSheet } = useApp();
  const [viewYear, setViewYear] = useState(selectedDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(selectedDate.getMonth());

  const grid = useMemo(() => buildMonthGrid(viewYear, viewMonth), [viewYear, viewMonth]);

  const eventsByDate = useMemo(() => {
    const map = {};
    for (const ev of events) {
      (map[ev.date] ??= []).push(ev);
    }
    return map;
  }, [events]);

  const goPrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const goNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const handleSelectDay = (day) => {
    setSelectedDate(day);
    openDayEventsSheet();
    if (day.getMonth() !== viewMonth || day.getFullYear() !== viewYear) {
      setViewMonth(day.getMonth());
      setViewYear(day.getFullYear());
    }
  };

  return (
    <>
      <div className="calendar-card">
        <div className="calendar-header">
          <div className="nav">
            <button type="button" onClick={goPrevMonth}>‹</button>
          </div>
          <div className="month-label">{formatMonthLabel(viewYear, viewMonth)}</div>
          <div className="nav">
            <button type="button" onClick={goNextMonth}>›</button>
          </div>
        </div>

        <div className="weekday-row">
          {WEEKDAYS.map((w) => (
            <span key={w}>{w}</span>
          ))}
        </div>

        <div className="day-grid">
          {grid.map((day) => {
            const key = dateKey(day);
            const dayEvents = eventsByDate[key] ?? [];
            const isOtherMonth = day.getMonth() !== viewMonth;
            const isToday = sameDate(day, today);
            const isSelected = sameDate(day, selectedDate);

            return (
              <div
                key={key}
                className={[
                  'day-cell',
                  isOtherMonth && 'other-month',
                  isToday && 'today',
                  isSelected && 'selected',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => handleSelectDay(day)}
              >
                <div className="num">{day.getDate()}</div>
                {dayEvents.length > 0 && (
                  // セルの高さは変えない前提のため、JS側では件数を絞らず全件
                  // レンダリングし、CSS側のmax-height + overflow:hiddenで
                  // 「セルに収まる範囲まで」だけを実際に見せる（「+n件」等の
                  // 省略表示は行わない、という指定どおり）。
                  <div className="day-events">
                    {dayEvents.map((ev, idx) => (
                      <div key={ev.id} className={`day-event-title order-${Math.min(idx + 1, ORDER_CLASS_COUNT)}`}>
                        {truncateTitle(ev.title)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
